/**
 * Capa offline-resiliente para GestiónAudio.
 *
 * - cacheGet(url, data): guarda respuesta GET para servirla offline
 * - readGetCache(url):    recupera la última respuesta cacheada de un GET
 * - queueMutation(req):   encola una mutación POST/PUT/PATCH/DELETE para reintento
 * - flushQueue(axios):    reintenta todas las mutaciones encoladas en orden FIFO
 * - getOnlineState() / subscribeOnlineState(cb): estado online/offline
 *
 * Se basa en localStorage (cola) + IndexedDB (cache de GETs grandes).
 */

const DB_NAME = "gestionaudio_offline_cache";
const STORE = "get_cache";
const QUEUE_KEY = "ga_offline_queue_v1";
const MAX_QUEUE_ITEMS = 200;
const MAX_CACHE_AGE_MS = 1000 * 60 * 60 * 24 * 7; // 7 días

// ---------- IndexedDB: cache de respuestas GET ----------
function openDB() {
  return new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) {
      reject(new Error("IndexedDB no disponible"));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function withStore(mode, cb) {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        const out = cb(store);
        tx.oncomplete = () => resolve(out);
        tx.onerror = () => reject(tx.error);
      })
  );
}

export async function cacheGet(url, data) {
  try {
    await withStore("readwrite", (store) => {
      store.put({ data, ts: Date.now() }, url);
    });
  } catch {
    /* offline cache opcional */
  }
}

export async function readGetCache(url) {
  try {
    const result = await withStore("readonly", (store) => {
      return new Promise((resolve) => {
        const r = store.get(url);
        r.onsuccess = () => resolve(r.result);
        r.onerror = () => resolve(null);
      });
    });
    const entry = await result;
    if (!entry) return null;
    if (Date.now() - entry.ts > MAX_CACHE_AGE_MS) return null;
    return { data: entry.data, ts: entry.ts };
  } catch {
    return null;
  }
}

export async function clearOfflineCache() {
  try {
    await withStore("readwrite", (store) => store.clear());
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(QUEUE_KEY);
  } catch {
    /* ignore */
  }
}

// ---------- Cola de mutaciones (localStorage) ----------
function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeQueue(items) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(items.slice(-MAX_QUEUE_ITEMS)));
  } catch {
    /* quota */
  }
}

export function queueMutation({ method, url, data, headers }) {
  const items = readQueue();
  const ctype = (headers && (headers["Content-Type"] || headers["content-type"])) || "";
  if (ctype.includes("multipart/form-data")) {
    // Ficheros no se pueden encolar (FormData no es serializable)
    throw new Error("offline: las subidas de archivos no pueden encolarse");
  }
  items.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    method,
    url,
    data: data || null,
    headers: headers || null,
    queued_at: new Date().toISOString(),
  });
  writeQueue(items);
  notifyQueueChange();
  return items.length;
}

export function getQueue() {
  return readQueue();
}

export function getQueueSize() {
  return readQueue().length;
}

export function clearQueue() {
  writeQueue([]);
  notifyQueueChange();
}

let flushing = false;
export async function flushQueue(api) {
  if (flushing) return { ok: 0, failed: 0, skipped: 0 };
  flushing = true;
  let ok = 0;
  let failed = 0;
  try {
    const items = readQueue();
    if (items.length === 0) return { ok: 0, failed: 0, skipped: 0 };
    const remaining = [];
    for (const item of items) {
      try {
        await api.request({
          method: item.method,
          url: item.url,
          data: item.data,
          headers: item.headers || undefined,
        });
        ok += 1;
      } catch (err) {
        const status = err?.response?.status;
        // Si es un 4xx (excepto 408/429) descartamos: la petición es inválida
        if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
          failed += 1;
        } else {
          remaining.push(item);
        }
      }
    }
    writeQueue(remaining);
    return { ok, failed, skipped: remaining.length };
  } finally {
    flushing = false;
    notifyQueueChange();
  }
}

// ---------- Estado online/offline ----------
const onlineListeners = new Set();
const queueListeners = new Set();
let currentOnline = typeof navigator !== "undefined" ? navigator.onLine : true;

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    currentOnline = true;
    onlineListeners.forEach((cb) => cb(true));
  });
  window.addEventListener("offline", () => {
    currentOnline = false;
    onlineListeners.forEach((cb) => cb(false));
  });
}

export function getOnlineState() {
  return currentOnline;
}

export function subscribeOnline(cb) {
  onlineListeners.add(cb);
  return () => onlineListeners.delete(cb);
}

export function subscribeQueue(cb) {
  queueListeners.add(cb);
  return () => queueListeners.delete(cb);
}

function notifyQueueChange() {
  queueListeners.forEach((cb) => cb(getQueueSize()));
}
